<script setup>
import { ref, onMounted, watch } from 'vue'

definePageMeta({
  layout: 'dashboard',
  middleware: 'auth',
  ssr: false,
})

const { $bgaApi } = useNuxtApp()

useHead({
  title: 'Händelselogg',
})

const transactionLogs = ref([])
const currentPage = ref(1)
const itemsPerPage = 50
const totalItems = ref(0)
const searchQuery = ref('')
const filterLogType = ref('')
const filterSubject = ref('')
const filterStartDate = ref('')
const filterEndDate = ref('')
const orderingField = ref('-created_at')
const typingTimer = ref(null)

const filtersOpen = ref(false)
const filtersBackup = ref(null)

// Flag to track if we should apply filters immediately
const applyFiltersImmediately = ref(false)

const formatDate = (dateString) => {
  const date = new Date(dateString)
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }
  return date.toLocaleString('sv-SE', options).replace(' ', ', ')
}

const logTypeVariant = (logType) => {
  if (logType === 'SUCCESS') return 'success'
  if (logType === 'INFO') return 'info'
  if (logType === 'ERROR') return 'warning'
  return 'secondary'
}

// Function to apply filters when the user clicks Applicera
const applyFilters = () => {
  applyFiltersImmediately.value = true
  fetchTransactionLogs(1)
  applyFiltersImmediately.value = false
  filtersOpen.value = false
}

// Function to backup filter state when modal opens
const backupFilters = () => {
  filtersBackup.value = {
    logType: filterLogType.value,
    subject: filterSubject.value,
    startDate: filterStartDate.value,
    endDate: filterEndDate.value,
    search: searchQuery.value,
  }
}

// Function to restore filter state when cancel is clicked
const restoreFilters = () => {
  if (filtersBackup.value) {
    filterLogType.value = filtersBackup.value.logType
    filterSubject.value = filtersBackup.value.subject
    filterStartDate.value = filtersBackup.value.startDate
    filterEndDate.value = filtersBackup.value.endDate
    searchQuery.value = filtersBackup.value.search
  }
  filtersOpen.value = false
}

const openFilters = () => {
  backupFilters()
  filtersOpen.value = true
}

// Clear all filters
const clearAllFilters = () => {
  filterLogType.value = ''
  filterSubject.value = ''
  filterStartDate.value = ''
  filterEndDate.value = ''
  searchQuery.value = ''

  // Fetch with cleared filters
  fetchTransactionLogs(1)
  filtersOpen.value = false
}

// Fetch transaction logs
const fetchTransactionLogs = async (page = 1) => {
  try {
    const params = {
      page,
      page_size: itemsPerPage,
      log_type: filterLogType.value,
      subject: filterSubject.value,
      created_at_after: filterStartDate.value,
      created_at_before: filterEndDate.value,
      search: searchQuery.value,
      ordering: orderingField.value,
    }

    const response = await $bgaApi.get('bga/transaction-logs', { params })
    transactionLogs.value = response.data.results
    totalItems.value = response.data.count
  } catch (error) {
    // console.error("Error fetching transaction logs:", error);
  }
}

// Change page function
const onClickHandler = (page) => {
  currentPage.value = page
  fetchTransactionLogs(page)
}

// Handle sorting
const handleSortClick = (field) => {
  orderingField.value = orderingField.value === field ? `-${field}` : field
  fetchTransactionLogs(currentPage.value)
}

// Initial fetch
onMounted(() => {
  // Set applyFiltersImmediately to true for initial load only
  applyFiltersImmediately.value = true
  fetchTransactionLogs()
  // Then set it back to false so future filter changes don't trigger API calls
  applyFiltersImmediately.value = false
})

// Watchers to fetch logs on filter change, but only when manually applied
watch([filterLogType, filterSubject, filterStartDate, filterEndDate, searchQuery], () => {
  // Only fetch if we should apply filters immediately
  if (applyFiltersImmediately.value) {
    fetchTransactionLogs(1)
  }
})

watch(searchQuery, () => {
  clearTimeout(typingTimer.value)
  typingTimer.value = setTimeout(() => {
    currentPage.value = 1
    fetchTransactionLogs(1)
  }, 600)
})
</script>

<template>
  <div class="dashboard-stack">
    <div class="dashboard-header">
      <div class="dashboard-header__titles">
        <h1 class="dashboard-page__title">Transaktionsloggar</h1>
        <p class="dashboard-page__subtitle lp-body">
          FÃ¥ överblick över systemhändelser och loggar.
        </p>
      </div>

      <div class="dashboard-header__actions">
        <AppButton variant="secondary" size="sm" type="button" @click="openFilters">
          <AppIcon name="hero-adjustments-horizontal" size="md" />
          Filter
        </AppButton>
      </div>
    </div>

    <AppListHeader
      :search="searchQuery"
      :items-per-page="''"
      search-placeholder="Sök i loggar"
      @update:search="(value) => (searchQuery = value)"
    />

    <AppCard class="app-card--table">
      <div class="app-table-wrap">
        <table class="app-table">
          <thead>
            <tr>
              <th @click="handleSortClick('created_at')">Datum & tid</th>
              <th @click="handleSortClick('subject')">Ã„mne</th>
              <th @click="handleSortClick('message')">Meddelande</th>
              <th @click="handleSortClick('log_type')">Typ</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in transactionLogs" :key="log.id">
              <td class="app-table__cell-strong">{{ formatDate(log.created_at) }}</td>
              <td class="app-table__cell-strong">{{ log.subject }}</td>
              <td>{{ log.message }}</td>
              <td >
                <AppChip :variant="logTypeVariant(log.log_type)">
                  {{ log.log_type }}
                </AppChip>
              </td>
            </tr>
            <tr v-if="!transactionLogs.length">
              <td class="app-table__empty" colspan="4">Inga loggar att visa.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <AppPagination
        :page="currentPage"
        :items-per-page="itemsPerPage"
        :total-items="totalItems"
        @update:page="onClickHandler"
      />
    </AppCard>

    <AppModal v-model="filtersOpen" title="Filtrera loggar">
      <div class="app-filters-grid">
        <AppSelect
          v-model="filterSubject"
          label="Ã„mne"
          :options="[
            { value: '', label: 'Alla' },
            { value: 'VOUCHER_POSTED', label: 'Verifikat har skapats' },
            { value: 'VOUCHER_NOT_POSTED', label: 'Verifikat kunde inte skapas' },
            { value: 'ACCOUNT_MISSING', label: 'Bokföringskonto saknas' },
            { value: 'ORDER_MISSING', label: 'Order saknas i Kodmyran' },
            { value: 'OTHER', label: 'Annat' },
          ]"
        />

        <AppSelect
          v-model="filterLogType"
          label="Händelsetyp"
          :options="[
            { value: '', label: 'Alla' },
            { value: 'SUCCESS', label: 'OK-loggar' },
            { value: 'ERROR', label: 'Fel-loggar' },
            { value: 'INFO', label: 'Informations-loggar' },
          ]"
        />

        <AppTextField v-model="filterStartDate" label="FrÃ¥n och med" type="date" />
        <AppTextField v-model="filterEndDate" label="Till och med" type="date" />
      </div>

      <template #footer>
        <AppButton variant="secondary" size="sm" type="button" @click="restoreFilters">
          Avbryt
        </AppButton>
        <AppButton variant="outline" size="sm" type="button" @click="clearAllFilters">
          Rensa filter
        </AppButton>
        <AppButton variant="primary" size="sm" type="button" @click="applyFilters">
          Applicera
        </AppButton>
      </template>
    </AppModal>
  </div>
</template>
